/**
 * CsvImportSection Component
 *
 * Handles CSV file upload and import for bank transactions.
 * Supports ING, Wise, and ING Credit Card CSV formats.
 */

import { useState, useCallback, useRef } from 'react';
import type ApiAccountingCsvImportLog from '@/types/serializers/ApiAccountingCsvImportLog';

/**
 * Available source types for CSV import
 */
const SOURCE_TYPES = [
  { value: 'ing', label: 'ING Bank', description: 'ING bank account transactions' },
  { value: 'wise', label: 'Wise', description: 'Wise (TransferWise) transactions' },
  { value: 'ing_credit_card', label: 'ING Credit Card', description: 'ING credit card statements' },
  { value: 'paypal', label: 'PayPal', description: 'PayPal transaction history' },
] as const;

/**
 * Result from the import API
 */
interface ImportResult {
  success: boolean;
  importedCount?: number;
  skippedCount?: number;
  errorCount?: number;
  parsedCount?: number;
  errors?: string[];
  error?: string;
  logId?: number;
}

/**
 * Props for CsvImportSection
 */
interface CsvImportSectionProps {
  onImportComplete?: (log: ApiAccountingCsvImportLog) => void;
}

/**
 * CsvImportSection provides a file upload interface for importing bank CSV files.
 */
export function CsvImportSection({ onImportComplete }: CsvImportSectionProps) {
  const [sourceType, setSourceType] = useState<string>('ing');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setResult(null);
  }, []);

  // Handle source type change
  const handleSourceChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceType(e.target.value);
    setResult(null);
  }, []);

  // Handle file upload and import
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('source_type', sourceType);

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

      const response = await fetch('/api/accounting/csv_import', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      });

      const data: ImportResult = await response.json();
      setResult(data);

      if (data.success && onImportComplete && data.logId) {
        // Fetch the log entry to pass to callback
        const historyResponse = await fetch('/api/accounting/csv_import/history', {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });
        const historyData = await historyResponse.json();
        const newLog = historyData.imports?.find((log: ApiAccountingCsvImportLog) => log.id === data.logId);
        if (newLog) {
          onImportComplete(newLog);
        }
      }

      // Clear file input on success
      if (data.success) {
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Import failed',
      });
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, sourceType, onImportComplete]);

  // Reset form
  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Import CSV File</h3>
            <p className="text-sm text-gray-500">Upload a CSV file from your bank to import transactions</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-4">
        {/* Source Type Selection */}
        <div>
          <label htmlFor="source-type" className="block text-sm font-medium text-gray-700 mb-1">
            Bank / Source
          </label>
          <select
            id="source-type"
            value={sourceType}
            onChange={handleSourceChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isUploading}
          >
            {SOURCE_TYPES.map((source) => (
              <option key={source.value} value={source.value}>
                {source.label} - {source.description}
              </option>
            ))}
          </select>
        </div>

        {/* File Input */}
        <div>
          <label htmlFor="csv-file" className="block text-sm font-medium text-gray-700 mb-1">
            CSV File
          </label>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              id="csv-file"
              accept=".csv"
              onChange={handleFileChange}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              disabled={isUploading}
            />
          </div>
          {selectedFile && (
            <p className="mt-1 text-sm text-gray-500">
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>

        {/* Result Message */}
        {result && (
          <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {result.success ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium text-green-800">Import Successful</span>
                </div>
                <div className="text-sm text-green-700 space-y-1">
                  <p>Parsed: {result.parsedCount} transactions</p>
                  <p>Imported: {result.importedCount} new transactions</p>
                  {(result.skippedCount ?? 0) > 0 && <p>Skipped: {result.skippedCount} duplicates</p>}
                  {(result.errorCount ?? 0) > 0 && <p>Errors: {result.errorCount}</p>}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-medium text-red-800">Import Failed</span>
                </div>
                <p className="text-sm text-red-700">{result.error}</p>
                {result.errors && result.errors.length > 0 && (
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {result.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
        {result && (
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Reset
          </button>
        )}
        <button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Importing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default CsvImportSection;
